import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Bots from "@/pages/Bots";
import CreateBot from "@/pages/CreateBot";
import BotDetail from "@/pages/BotDetail";
import TelegramAccounts from "@/pages/TelegramAccounts";
import AddAccount from "@/pages/AddAccount";
import AccountDetail from "@/pages/AccountDetail";
import Templates from "@/pages/Templates";
import Automations from "@/pages/Automations";
import Jobs from "@/pages/Jobs";
import Broadcasts from "@/pages/Broadcasts";
import Analytics from "@/pages/Analytics";
import Logs from "@/pages/Logs";
import Security from "@/pages/Security";
import Capabilities from "@/pages/Capabilities";
import Settings from "@/pages/Settings";
import RunsMonitor from "@/pages/journeys/RunsMonitor";
import RunDetail from "@/pages/journeys/RunDetail";
import FlowBuilder from "@/pages/journeys/FlowBuilder";
import AIMedia from "@/pages/AIMedia";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const sid = hash.split("session_id=")[1]?.split("&")[0];
    (async () => {
      try {
        if (sid) await api.post("/auth/session", { session_id: sid });
      } catch (e) { /* fall through */ }
      window.history.replaceState(null, "", window.location.pathname);
      window.location.replace("/");
    })();
  }, [navigate]);
  return <Loading />;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/accounts" element={<TelegramAccounts />} />
        <Route path="/accounts/add" element={<AddAccount />} />
        <Route path="/accounts/:id" element={<AccountDetail />} />
        <Route path="/bots" element={<Bots />} />
        <Route path="/bots/create" element={<CreateBot />} />
        <Route path="/bots/:id" element={<BotDetail />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/automations" element={<Automations />} />
        <Route path="/journeys" element={<RunsMonitor />} />
        <Route path="/journeys/:runId" element={<RunDetail />} />
        <Route path="/bots/:id/flows/:workflowId" element={<FlowBuilder />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/broadcasts" element={<Broadcasts />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/security" element={<Security />} />
        <Route path="/capabilities" element={<Capabilities />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/ai-media/:page" element={<AIMedia />} />
        <Route path="/ai-media" element={<AIMedia />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
